import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { isBackendUnreachableError } from '../utils/backendStatus';
import { formatApiError, formatAxiosError } from '../utils/apiError';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        // First try to get user from localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (error) {
            console.error('Error parsing stored user:', error);
          }
        }
        
        // Then fetch fresh profile from API
        try {
          const response = await authAPI.getProfile();
          console.log('Profile API response:', response.data);
          const userData = response.data;
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
        } catch (error) {
          console.error('Profile fetch error:', error);
          // Keep the cached session when the platform is down so users
          // see the system-unavailable screen instead of a silent logout.
          if (!isBackendUnreachableError(error)) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      console.log('Attempting login with email:', email);
      const response = await authAPI.login(email, password);
      console.log('Login API response:', response.data);
      const { token: newToken, user: userData } = response.data;
      console.log('Extracted user data:', userData);
      
      // Store both token and user data in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);
      
      // Check if password change is required
      if (userData.mustChangePassword) {
        return { success: true, user: userData, mustChangePassword: true };
      }
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      
      // Extract error message with better handling
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error.response) {
        // Server responded with error
        if (error.response.data?.detail) {
          errorMessage = formatApiError(error.response.data.detail, errorMessage);
        } else if (error.response.data?.message) {
          errorMessage = formatApiError(error.response.data.message, errorMessage);
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.status === 401) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (error.response.status === 503) {
          errorMessage = 'Service unavailable. Please check if backend services are running.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Unable to reach server. Please check if backend services are running.';
      } else {
        // Error setting up request
        errorMessage = error.message || 'An unexpected error occurred.';
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const refreshProfile = async () => {
    if (!token) {
      return null;
    }
    try {
      const response = await authAPI.getProfile();
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      return null;
    }
  };

  const updateUser = (updater) => {
    setUser((previous) => {
      const nextValue = typeof updater === 'function' ? updater(previous) : updater;
      if (nextValue) {
        localStorage.setItem('user', JSON.stringify(nextValue));
      } else {
        localStorage.removeItem('user');
      }
      return nextValue;
    });
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Registration error:', error.response?.data || error);
      const errorMessage = formatAxiosError(error, 'Registration failed');
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshProfile,
    updateUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
